import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

/**
 * Creates a notification in Firestore
 * @param {string} userId - The user ID to notify
 * @param {string} message - The notification message
 * @param {string} type - The notification type (comment, vote, award)
 * @param {string} link - The link to navigate to when clicked
 * @param {Object} additionalData - Any additional data to include
 * @returns {Promise<{success: boolean, notificationId?: string, error?: string}>}
 */
export const createNotification = async (userId, message, type, link, additionalData = {}) => {
  try {
    if (!userId) {
      console.error('Attempted to create notification for undefined userId');
      return { success: false, error: 'No user ID provided' };
    }

    console.log(`Creating notification for user ${userId}: ${message} (type: ${type})`);
    
    const notificationData = {
      userId,
      message,
      type,
      link,
      read: false,
      createdAt: serverTimestamp(),
      ...additionalData
    };

    console.log(`Creating notification with data:`, JSON.stringify(notificationData));
    const docRef = await addDoc(collection(db, 'notifications'), notificationData);
    console.log(`Notification created with ID: ${docRef.id}`);
    return { success: true, notificationId: docRef.id };
  } catch (error) {
    console.error('Error creating notification:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Create a notification for when a user receives a new comment on their query
 */
export const createCommentNotification = async (userId, commenterName, queryId, queryTitle) => {
  const message = `${commenterName} commented on your query: ${queryTitle}`;
  const link = `/query/${queryId}`;
  return createNotification(userId, message, 'comment', link, { queryId });
};

/**
 * Create a notification for when a user receives a reply to their comment
 */
export const createReplyNotification = async (userId, replierName, queryId, queryTitle) => {
  const message = `${replierName} replied to your comment on: ${queryTitle}`;
  const link = `/query/${queryId}`;
  return createNotification(userId, message, 'comment', link, { queryId });
};

/**
 * Create a notification for when a user's query receives a vote
 */
export const createVoteNotification = async (userId, voterName, queryId, queryTitle, isUpvote) => {
  const voteType = isUpvote ? 'upvoted' : 'downvoted';
  const message = `${voterName} ${voteType} your query: ${queryTitle}`;
  const link = `/query/${queryId}`;
  return createNotification(userId, message, 'vote', link, { queryId, isUpvote });
};

/**
 * Create a notification for when a user's comment receives a vote
 */
export const createCommentVoteNotification = async (userId, voterName, queryId, queryTitle, isUpvote) => {
  const voteType = isUpvote ? 'upvoted' : 'downvoted';
  const message = `${voterName} ${voteType} your comment on: ${queryTitle}`;
  const link = `/query/${queryId}`;
  return createNotification(userId, message, 'vote', link, { queryId, isUpvote });
};

/**
 * Create a notification for when a user receives points or an award
 */
export const createAwardNotification = async (userId, pointsAmount, reason) => {
  const message = `You received ${pointsAmount} points for ${reason}!`;
  const link = `/profile`;
  return createNotification(userId, message, 'award', link, { pointsAmount });
};

/**
 * Create a notification for when a user unlocks a feature
 */
export const createFeatureUnlockNotification = async (userId, featureName) => {
  const message = `You've unlocked access to ${featureName}!`;
  const link = `/profile`;
  return createNotification(userId, message, 'award', link, { featureName });
};

/**
 * Create a notification confirming a report was submitted
 */
export const createReportSubmissionNotification = async (userId, contentType) => {
  const message = `Your report for a ${contentType} has been submitted and will be reviewed.`;
  const link = `/`;
  return createNotification(userId, message, 'report', link, { contentType });
};

/**
 * Create a notification for report outcome
 */
export const createReportOutcomeNotification = async (userId, contentType, wasRemoved) => {
  const message = wasRemoved 
    ? `A ${contentType} you reported has been reviewed and removed for violating our community guidelines.`
    : `A ${contentType} you reported has been reviewed and was found not to violate our community guidelines.`;
  const link = `/`;
  return createNotification(userId, message, 'report', link, { contentType, wasRemoved });
};

/**
 * Create a notification for when a user's content is reported and removed
 */
export const createContentRemovedNotification = async (userId, contentType, queryId, reason) => {
  if (!userId) {
    console.error('Attempted to create content removal notification with no userId');
    return { success: false, error: 'No user ID provided' };
  }

  try {
    const message = `Your ${contentType} was removed for violating our community guidelines: ${reason}`;
    const link = queryId ? `/query/${queryId}` : '/';
    
    // Explicitly make canAppeal true to ensure users can appeal
    const result = await createNotification(
      userId, 
      message, 
      'moderation', 
      link, 
      { 
        contentType, 
        queryId, 
        canAppeal: true,
        removalReason: reason,
        removedAt: new Date()
      }
    );
    
    console.log(`Content removal notification created: ${result.success ? 'success' : 'failed'}`);
    return result;
  } catch (error) {
    console.error('Error creating content removal notification:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Create a notification for admin about a new appeal
 */
export const createAppealNotificationForAdmin = async (adminId, contentType, appealId) => {
  const message = `A user has appealed a ${contentType} removal. Please review this appeal.`;
  const link = `/admin/appeals/${appealId}`;
  return createNotification(adminId, message, 'appeal', link, { appealId, requiresAction: true });
};

/**
 * Create a notification for user about appeal outcome
 */
export const createAppealOutcomeNotification = async (userId, contentType, wasApproved) => {
  const message = wasApproved 
    ? `Your appeal for a removed ${contentType} has been approved. Your content has been restored.`
    : `Your appeal for a removed ${contentType} has been denied. The content will remain removed.`;
  const link = `/`;
  return createNotification(userId, message, 'moderation', link, { contentType, wasApproved });
}; 